import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, Tag, ChevronRight, ShoppingBag } from "lucide-react";
import { useCart } from "../lib/cartContext";
import { formatSGD } from "../lib/mockData";
import { toast } from "sonner";

export default function Cart() {
  const {
    detailed,
    subtotal,
    discount,
    deliveryFee,
    total,
    appliedPromo,
    freeDeliveryThreshold,
    updateQty,
    removeItem,
    applyPromo,
    clearPromo,
  } = useCart();
  const [code, setCode] = useState("");

  const onApply = () => {
    if (!code.trim()) return;
    const res = applyPromo(code);
    if (res.ok) {
      toast.success(`Promo code ${res.promo.code} applied`);
    } else if (res.reason === "min") {
      toast.error(
        `This code requires a minimum spend of ${formatSGD(res.minPurchase)}. Add ${formatSGD(res.shortfall)} more to use it.`
      );
    } else if (res.reason === "noEligible") {
      toast.error("This code doesn't apply to any items in your cart.");
    } else {
      toast.error("Invalid or expired promo code");
    }
  };

  if (detailed.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag size={48} className="mx-auto text-gold" strokeWidth={1.4} />
        <h1 className="font-serif text-4xl text-emerald mt-6">Your cart is empty</h1>
        <p className="text-emerald-light/80 mt-3">Discover our curated halal gift hampers.</p>
        <Link
          to="/shop"
          className="mt-6 inline-flex items-center gap-2 bg-emerald text-cream px-6 py-3 text-sm hover:bg-emerald-dark transition-colors"
        >
          Browse Hampers <ChevronRight size={14} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="cart-page">
      <h1 className="font-serif text-4xl md:text-5xl text-emerald">
        Your <span className="italic text-gold">Cart</span>
      </h1>
      <p className="text-emerald-light/70 mt-2">{detailed.length} item(s) in cart</p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10">
        {/* Items */}
        <div className="space-y-4">
          {detailed.map((i) => (
            <div
              key={i.productId}
              className="flex flex-col sm:flex-row gap-5 bg-white border border-gold/25 rounded-xl p-5"
              data-testid={`cart-item-${i.productId}`}
            >
              <Link
                to={`/product/${i.product.slug}`}
                className="w-32 h-32 shrink-0 rounded-lg overflow-hidden bg-cream-2 p-2 mx-auto sm:mx-0"
              >
                <img
                  src={i.product.images[0]}
                  alt={i.product.name}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </Link>
              <div className="flex-1 flex flex-col justify-between gap-3">
                <div className="flex justify-between gap-3">
                  <div>
                    <Link to={`/product/${i.product.slug}`}>
                      <h3 className="font-serif text-xl text-emerald hover:text-gold transition-colors">
                        {i.product.name}
                      </h3>
                    </Link>
                    <div className="text-xs text-emerald-light/70 mt-1">SKU: {i.product.sku}</div>
                  </div>
                  <button
                    onClick={() => removeItem(i.productId)}
                    className="text-emerald-light/60 hover:text-red-600 transition-colors p-1"
                    aria-label="remove"
                    data-testid={`cart-remove-${i.productId}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="inline-flex items-center border border-gold/40 rounded-lg">
                    <button
                      onClick={() => updateQty(i.productId, i.qty - 1)}
                      className="px-3 py-2 hover:bg-gold/10"
                      data-testid={`cart-decrease-${i.productId}`}
                    >
                      <Minus size={12} />
                    </button>
                    <div className="px-4 min-w-[2.5rem] text-center text-sm">{i.qty}</div>
                    <button
                      onClick={() => updateQty(i.productId, i.qty + 1)}
                      className="px-3 py-2 hover:bg-gold/10"
                      data-testid={`cart-increase-${i.productId}`}
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-emerald">{formatSGD(i.lineTotal)}</div>
                    <div className="text-xs text-emerald-light/70">{formatSGD(i.unitPrice)} each</div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <Link
            to="/shop"
            className="inline-flex items-center gap-2 text-emerald hover:text-gold transition-colors text-sm mt-2"
            data-testid="continue-shopping-link"
          >
            ← Continue Shopping
          </Link>
        </div>

        {/* Summary */}
        <aside className="bg-white border border-gold/25 rounded-xl p-6 h-fit sticky top-32" data-testid="cart-summary">
          <h3 className="font-serif text-2xl text-emerald">Order Summary</h3>

          {/* Promo */}
          <div className="mt-5">
            <label className="text-xs uppercase tracking-widest text-emerald-light/70 font-medium">
              Promo Code
            </label>
            <div className="mt-2 flex gap-2">
              <div className="relative flex-1">
                <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-emerald/60" />
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="RAYA25"
                  className="w-full pl-9 pr-3 py-2.5 bg-cream-2 border border-gold/25 rounded-lg text-sm outline-none focus:ring-2 focus:ring-gold"
                  data-testid="promo-code-input"
                />
              </div>
              <button
                onClick={onApply}
                className="px-4 py-2.5 bg-emerald text-cream text-sm hover:bg-emerald-dark transition-colors"
                data-testid="apply-promo-btn"
              >
                Apply
              </button>
            </div>
            {appliedPromo && (
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-gold">
                  ✓ {appliedPromo.code} applied
                </span>
                <button
                  onClick={() => {
                    clearPromo();
                    setCode("");
                    toast("Promo removed");
                  }}
                  className="text-emerald-light/70 hover:text-red-600"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 space-y-3 text-sm">
            <div className="flex justify-between text-emerald-light/80">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">{formatSGD(subtotal)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-gold font-medium">
                <span>Discount</span>
                <span data-testid="cart-discount">− {formatSGD(discount)}</span>
              </div>
            )}
            <div className="flex justify-between text-emerald-light/80">
              <span>Delivery Fee</span>
              <span data-testid="cart-delivery">
                {deliveryFee === 0 ? "Free" : formatSGD(deliveryFee)}
              </span>
            </div>
            {deliveryFee > 0 && (
              <div className="text-xs text-emerald-light/60">
                Add {formatSGD(freeDeliveryThreshold - subtotal)} more for free delivery
              </div>
            )}
            <div className="border-t border-gold/20 pt-3 flex justify-between font-serif text-2xl text-emerald">
              <span>Total</span>
              <span data-testid="cart-total">{formatSGD(total)}</span>
            </div>
          </div>

          <Link
            to="/checkout"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold text-emerald px-6 py-3.5 text-sm font-semibold hover:bg-gold-light transition-colors"
            data-testid="proceed-to-checkout-btn"
          >
            Proceed to Checkout <ChevronRight size={16} />
          </Link>
          <Link
            to="/shop"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-emerald/30 text-emerald px-6 py-3 text-sm hover:bg-emerald/5 transition-colors"
          >
            Continue Shopping
          </Link>
        </aside>
      </div>
    </div>
  );
}
